import React, { useState, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { reduxForm } from 'redux-form';
import { Link, useHistory, useLocation, Redirect } from 'react-router-dom';
import { Button, Form, FormGroup, Row, Col } from 'reactstrap';
import * as selectors from 'redux-modules/auth/selectors';
import { togglePasswordVisibility, checkMobilePrompt } from 'redux-modules/auth/actions';
import AuthWrapper from 'components/AuthWrapper';
import BlockingSpinnerOverlay from 'components/BlockingSpinnerOverlay';
import PasswordField from 'components/fields/PasswordField';
import AuthWrapperFooter from 'components/AuthWrapperFooter';
import HpaStoreContext from 'contexts/HpaStoreContext';
import identityClient from '../../clients/identityClient';
import FormatErrorMessage from '../FormatMessage/FormatErrorMessage';
import errorMessages from 'helpers/errorMessages';
import { shouldSuppressRegister } from '../../helpers/customBranding';
import { buildSignUpSearchFromLocation } from '../../helpers/buildSignUpSearchFromLocation';

const LoginWithPassword = ({ handleSubmit, ssoIntegrationOptions }) => {
    const user = useSelector(selectors.getUser);
    const isPasswordVisible = useSelector(selectors.isPasswordVisible);
    const context = useContext(HpaStoreContext);
    const dispatch = useDispatch();
    const history = useHistory();
    const [errorStatus, setErrorStatus] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const promptForPhone = useSelector(selectors.getPromptForPhone);
    const [displayAssistance, setDisplayAssistance] = useState(false);

    const locationHook = useLocation();
    const { primaryUserName, newSignupFlow } = locationHook.state || {};
    const params = locationHook.search;

    useEffect(() => {
        if (promptForPhone) {
            history.push({
                pathname: '/phonePrompt',
                search: params,
                state: { ssoIntegrationOptions: context.customBranding },
            });
        }
    }, [promptForPhone, history, params, context.customBranding]);

    const toRegister = {
        pathname: newSignupFlow && newSignupFlow.value === "1" ? '/account/signup' : '/signup',
        search: buildSignUpSearchFromLocation(params, {
            ...(ssoIntegrationOptions || {}),
            username:
                locationHook.state?.username ||
                context.username ||
                ssoIntegrationOptions?.username,
        }),
    };
    const toResetPassword = {
        pathname: '/forgotPassword',
        search: params,
    };
    const toLogin = {
        pathname: '/login',
        search: params,
    };

    const handleTogglePasswordVisibility = () => {
        dispatch(togglePasswordVisibility());
    };

    const displayUserName = decodeURIComponent(primaryUserName);

    const toggleDisplayAssistance = (e) => {
        if (e) e.preventDefault();
        setDisplayAssistance(!displayAssistance);
    };

    const handleFormSubmit = async (values) => {
        if (isSubmitting) return;
        const { loginPassword } = values;
        setIsSubmitting(true);
        try {
            const queryString = params ? `${params}` : '';
            await identityClient.oauthLogin(displayUserName, loginPassword, queryString).then(async (response) => {
                if (response.emails.length > 0 || response.phoneNumbers.length > 0) {
                    history.push({
                        pathname: '/verifyIdentity',
                        search: queryString,
                        state: { emails: response.emails, phoneNumbers: response.phoneNumbers, verifyInfo: response, queryString },
                    });
                } else {
                    await dispatch(checkMobilePrompt(queryString));
                }
            });
            setErrorStatus(false);
            setErrorMessage(undefined);
        } catch (err) {
            if (err.response && err.response.data && err.response.data.status === 401 && (err.response.data.detail === "CSRF token header missing" || err.response.data.detail === "CSRF cookie missing" || err.response.data.detail === "CSRF token header and cookie don't match")) {
                setErrorMessage(errorMessages.configError);
                setErrorStatus(true);
            }
            else if (err.response && err.response.data && err.response.data.status === 401 && err.response.data.detail === "Incorrect username or password.") {
                setErrorMessage(errorMessages.invalidLogin);
                setErrorStatus(true);
            }
            else if (err.response && err.response.data && err.response.data.status === 400 && err.response.data.title === "Password Expired") {
                setErrorMessage(errorMessages.expiredPassword);
                setErrorStatus(true);
            }

            else if (err.response && err.response.data && err.response.data.status === 400 && err.response.data.title === "Forcing Password") {
                setErrorMessage(errorMessages.forceResetPassword);
                setErrorStatus(true);
            }

            else if (err.response && err.response.data && err.response.data.status === 400 && err.response.data.title === "Account Locked") {
                setErrorMessage(errorMessages.accountLocked);
                setErrorStatus(true);
            }
            else {
                setErrorMessage(errorMessages.invalidLogin);
                setErrorStatus(true);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const shouldSuppressRegisterFlag = shouldSuppressRegister(context);
    return user && user.isRegistered ? (
        <Redirect push to="/" />
    ) : (
        <AuthWrapper
            footer={<AuthWrapperFooter displayAssistance={displayAssistance} toggleDisplayAssistance={toggleDisplayAssistance} />}
        >
            {errorStatus && <FormatErrorMessage
                error={errorMessage.message}
                recoverErrorCode={errorMessage.message}
                recoverEmail={primaryUserName}
                errorType={errorMessage.type}
                toggleDisplayAssistance={toggleDisplayAssistance}
            />}

            <p style={{ fontSize: '17px' }}>Logging in as <b>{displayUserName}</b></p>
            <Form
                className="position-relative"
                aria-busy={isSubmitting}
                onSubmit={handleSubmit(handleFormSubmit)}
            >
                <BlockingSpinnerOverlay show={isSubmitting} ariaLabel="Loading..." />
                <PasswordField
                    displayPassword={isPasswordVisible}
                    onToggle={handleTogglePasswordVisibility}
                    showTooltip={false}
                    skipValidation
                    current
                    name="loginPassword"
                />
                <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ margin: '-.5em 0 1rem' }}
                >
                    <Link
                        to={toResetPassword}
                        className="text-muted text-sm"
                        tabIndex={isSubmitting ? -1 : 0}
                        aria-disabled={isSubmitting || undefined}
                        onClick={(e) => {
                            if (isSubmitting) e.preventDefault();
                        }}
                    >
                        Forgot your password?
                    </Link>
                </div>
                <FormGroup>
                    <Button
                        className="btn"
                        block
                        color="primary"
                        disabled={isSubmitting}
                        type="submit"
                    >
                        Continue
                    </Button>
                </FormGroup>
            </Form>
            <>
                <hr />
                <Row>
                    {(shouldSuppressRegisterFlag === undefined || shouldSuppressRegisterFlag === null || shouldSuppressRegisterFlag === false) && (
                        <Col>
                            <section className="d-flex align-items-center">
                                <span style={{ marginRight: '.5em' }}>Need to sign up?</span>
                                <Link
                                    to={toRegister}
                                    tabIndex={isSubmitting ? -1 : 0}
                                    aria-disabled={isSubmitting || undefined}
                                    onClick={(e) => {
                                        if (isSubmitting) e.preventDefault();
                                    }}
                                >
                                    Register Now
                                </Link>
                            </section>
                        </Col>
                    )}
                    <Col>
                        <section className="d-flex align-items-center float-right">
                            <Link
                                to={toLogin}
                                id="loginWithDifferentUser"
                                tabIndex={isSubmitting ? -1 : 0}
                                aria-disabled={isSubmitting || undefined}
                                onClick={(e) => {
                                    if (isSubmitting) e.preventDefault();
                                }}
                            >
                                Login with different user
                            </Link>
                        </section>
                    </Col>
                </Row>
            </>

        </AuthWrapper>
    );
};

LoginWithPassword.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    primaryUserName: PropTypes.string.isRequired,
    ssoIntegrationOptions: PropTypes.shape({
        username: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
    }),
};

LoginWithPassword.defaultProps = {
    ssoIntegrationOptions: undefined,
};

const ConnectedPasswordForm = reduxForm({
    form: 'password',
})(LoginWithPassword);

export default ConnectedPasswordForm;
export { LoginWithPassword as PurePasswordForm };
